import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Input } from '@/components/core/Input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/core/Select';
import { Textarea } from '@/components/core/Textarea';
import { useDataModel } from '@/hooks/useDataModel';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import { isContactEmailValid } from './businessPartner';
import {
    EMAIL_INVITATION_TYPES,
    EmailInvitationType,
    rememberInvitationType,
    storedInvitationType,
} from './invitation';
import { useSaveEmailInvitation } from './invitation.queries';
import { InvitationCustomAttributes, readContactAttributes } from './InvitationCustomAttributes';

interface SendEmailInvitationDialogProps {
    open: boolean;
    onClose: () => void;
}

const keepSelectOpen = (event: { target: EventTarget | null; preventDefault: () => void }) => {
    if (event.target instanceof Element && event.target.closest('.select-content')) {
        event.preventDefault();
    }
};

const SendEmailInvitationDialog = ({ open, onClose }: SendEmailInvitationDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { dataModel } = useDataModel();
    const attributes = useMemo(() => readContactAttributes(dataModel), [dataModel]);
    const form = useForm();
    const { reset, getValues } = form;
    const [email, setEmail] = useState('');
    const [invitationType, setInvitationType] = useState<EmailInvitationType>('INVITED_SUPPLIER');
    const [text, setText] = useState('');
    const saveInvitation = useSaveEmailInvitation();
    const emailValid = email.trim().length > 0 && isContactEmailValid(email);
    const showEmailError = email.trim().length > 0 && !isContactEmailValid(email);

    useEffect(() => {
        if (!open) {
            return;
        }
        setEmail('');
        setInvitationType(storedInvitationType());
        setText('');
        reset({});
    }, [open, reset]);

    const save = () => {
        if (!emailValid || saveInvitation.isPending) {
            return;
        }
        rememberInvitationType(invitationType);
        const customValues: Record<string, unknown> = {};
        const values = getValues();
        attributes.forEach((attribute) => {
            if (values[attribute.name] !== undefined) {
                customValues[attribute.name] = values[attribute.name];
            }
        });
        saveInvitation.mutate({
            invitedEmailAddress: email.trim(),
            invitationType,
            text,
            customValues,
        }, {
            onSuccess: () => {
                toast.success('INVITATION_SUCCESSFULLY_SENT');
                onClose();
            },
            onError: () => {
                toast.error('SAVE_ERROR_MESSAGE');
            },
        });
    };

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent
                variant="large"
                onPointerDownOutside={keepSelectOpen}
                onInteractOutside={keepSelectOpen}
            >
                <DialogHeader>
                    <DialogHeaderDefault closeLabel={t('CLOSE')}>
                        {t('SEND_EMAIL_INVITATION')}
                    </DialogHeaderDefault>
                </DialogHeader>
                <DialogBody className="tw-max-h-[70vh] tw-overflow-y-auto">
                    <FormProvider {...form}>
                        <form className="email-invitation tw-flex tw-flex-col tw-gap-4" onSubmit={(event) => event.preventDefault()}>
                            <label className="tw-flex tw-flex-col tw-gap-1" htmlFor="invitation-email-address">
                                <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                    {t('ENTER_EMAIL_ADDRESS')}
                                </span>
                                <Input
                                    id="invitation-email-address"
                                    name="invitation_email_address"
                                    type="email"
                                    value={email}
                                    required
                                    aria-invalid={showEmailError}
                                    autoFocus
                                    onChange={(event) => setEmail(event.target.value)}
                                />
                                {showEmailError && (
                                    <span className="tw-text-t-sm tw-text-error lg:tw-text-t-base">
                                        {t('BILLING.MUST_BE_A_VALID_EMAIL_FORMAT')}
                                    </span>
                                )}
                            </label>
                            <label className="tw-flex tw-flex-col tw-gap-1" htmlFor="invitation-invitee-type">
                                <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('INVITE_AS')}</span>
                                <Select value={invitationType} onValueChange={(value) => setInvitationType(value as EmailInvitationType)}>
                                    <SelectTrigger id="invitation-invitee-type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {EMAIL_INVITATION_TYPES.map((type) => (
                                            <SelectItem key={type} value={type}>{t(type)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </label>
                            <label className="tw-flex tw-flex-col tw-gap-1" htmlFor="invitation-reason-input">
                                <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                    {t('REASON_FOR_INVITE')}
                                </span>
                                <Textarea
                                    id="invitation-reason-input"
                                    rows={6}
                                    value={text}
                                    onChange={(event) => setText(event.target.value)}
                                />
                            </label>
                            <InvitationCustomAttributes />
                        </form>
                    </FormProvider>
                </DialogBody>
                <DialogFooter>
                    <Button
                        id="submit-invitation"
                        type="button"
                        disabled={!emailValid}
                        loading={saveInvitation.isPending}
                        onClick={save}
                    >
                        {t('SEND_INVITATION')}
                    </Button>
                    <Button type="button" variant="secondary" onClick={onClose} disabled={saveInvitation.isPending}>
                        {t('CANCEL')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { SendEmailInvitationDialog };
